import {
    APIProcessName,
    SQSProcessName,
} from "@common/constants/process-names";
import { DocumentSource } from "@common/constants/financial-documents";
import { FinancialAccountSourceType } from "@common/types/financialAccount";
import { FIELD_UPDATE_SOURCE } from "@common/constants";
import type { InstitutionKey } from "@design-system/InstitutionIcon/InstitutionIcon";

export interface IntegrationFilterOption {
    label: string;
    institution: InstitutionKey;
    sourceTypes: string[];
    systemSources: FIELD_UPDATE_SOURCE[];
    accountSourceType: FinancialAccountSourceType;
    documentSource?: DocumentSource;
}

// Single source of truth for every integration. Each descriptor ties together
// the audit-log source types it produces, the `system` values it stamps on its
// own self-performed rows, its account source type, and (optionally) its
// document source. Deriving the per-source-type and filter lookups from this
// one list keeps them from drifting (e.g. a source type with no system source).
const INTEGRATION_DESCRIPTORS: IntegrationFilterOption[] = [
    {
        label: "Plaid",
        institution: "plaid",
        sourceTypes: [
            SQSProcessName.PLAID_INITIAL_TRANSACTION_IMPORT,
            SQSProcessName.PLAID_TRANSACTION_IMPORT,
            APIProcessName.PLAID_WEBHOOK_SYNC_UPDATES_AVAILABLE,
            APIProcessName.PLAID_WEBHOOK_DEFAULT_UPDATE,
            APIProcessName.PLAID_WEBHOOK_HISTORICAL_UPDATE,
            APIProcessName.PLAID_WEBHOOK_TRANSACTIONS_REMOVED,
        ],
        systemSources: [FIELD_UPDATE_SOURCE.PLAID_IMPORT],
        accountSourceType: FinancialAccountSourceType.PLAID,
        documentSource: DocumentSource.PLAID,
    },
    {
        label: "Stripe",
        institution: "stripe",
        sourceTypes: [
            SQSProcessName.STRIPE_INVOICES_SYNC,
            SQSProcessName.STRIPE_TRANSACTIONS_SYNC,
            SQSProcessName.STRIPE_SYNC,
        ],
        systemSources: [FIELD_UPDATE_SOURCE.STRIPE],
        accountSourceType: FinancialAccountSourceType.STRIPE,
    },
    {
        label: "PayPal",
        institution: "paypal",
        sourceTypes: [SQSProcessName.PAYPAL_SYNC],
        systemSources: [FIELD_UPDATE_SOURCE.PAYPAL],
        accountSourceType: FinancialAccountSourceType.PAYPAL,
    },
    {
        label: "Mercury",
        institution: "mercury",
        sourceTypes: [SQSProcessName.MERCURY_ACCOUNTS_SYNC],
        systemSources: [FIELD_UPDATE_SOURCE.MERCURY],
        accountSourceType: FinancialAccountSourceType.MERCURY,
        documentSource: DocumentSource.MERCURY,
    },
    {
        label: "Gusto",
        institution: "gusto",
        sourceTypes: [SQSProcessName.GUSTO_ACCOUNT_SYNC],
        systemSources: [FIELD_UPDATE_SOURCE.GUSTO],
        accountSourceType: FinancialAccountSourceType.GUSTO,
    },
    {
        label: "ADP RUN",
        institution: "adp",
        sourceTypes: [SQSProcessName.ADP_RUN_ACCOUNT_SYNC],
        systemSources: [FIELD_UPDATE_SOURCE.ADP_RUN],
        accountSourceType: FinancialAccountSourceType.ADP_RUN,
    },
    {
        label: "Ramp",
        institution: "ramp",
        sourceTypes: [
            SQSProcessName.RAMP_TRANSACTIONS_SYNC,
            SQSProcessName.RAMP_BILLS_SYNC,
            SQSProcessName.RAMP_ACCOUNTS_SYNC,
        ],
        systemSources: [FIELD_UPDATE_SOURCE.RAMP],
        accountSourceType: FinancialAccountSourceType.RAMP,
        documentSource: DocumentSource.RAMP,
    },
    {
        label: "Bill.com",
        institution: "bill",
        sourceTypes: [SQSProcessName.BILLCOM_SYNC],
        systemSources: [FIELD_UPDATE_SOURCE.BILLCOM],
        accountSourceType: FinancialAccountSourceType.BILLCOM,
    },
];

export const integrationActors: Record<
    string,
    Pick<IntegrationFilterOption, "institution" | "label">
> = Object.fromEntries(
    INTEGRATION_DESCRIPTORS.flatMap(({ institution, label, sourceTypes }) =>
        sourceTypes.map((sourceType) => [sourceType, { institution, label }]),
    ),
);

export const UNIQUE_INTEGRATIONS: IntegrationFilterOption[] =
    INTEGRATION_DESCRIPTORS;

export const DOCUMENT_SOURCE_INTEGRATIONS = UNIQUE_INTEGRATIONS.filter(
    (i): i is IntegrationFilterOption & { documentSource: DocumentSource } =>
        !!i.documentSource,
);
